import { STORAGE_KEYS } from '@/constants';

/**
 * Where tokens live, and why.
 *
 * The access token is kept **in memory only**. It never touches localStorage, so
 * a cross-site scripting bug cannot read it out of storage, and it dies with the
 * tab.
 *
 * The refresh token is not here at all. It lives in an httpOnly cookie that the
 * server sets and that no script on this page can read - not this module, not a
 * dependency, not an injected one. That is the point: the long-lived credential
 * is the one worth stealing, and the only way to make it unstealable by script
 * is for script never to see it.
 *
 * What that costs us is the ability to ask "am I signed in?" without a network
 * call, because the cookie is invisible from here. Hence the hint below.
 */
let accessToken: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * A flag saying a session probably exists, so boot can skip a pointless refresh.
 *
 * Deliberately not a credential and not treated as one: it grants nothing, and
 * the server decides whether the cookie is real. It exists only so a first-time
 * visitor does not pay for a request that is certain to 401, and so somebody
 * returning does not see the login form flash before their session restores.
 *
 * Being wrong in either direction is harmless. Stale-true costs one failed
 * refresh; stale-false shows a login form to somebody who could have been
 * restored, and they log in again.
 */
export function markSessionLikely(): void {
  try {
    localStorage.setItem(STORAGE_KEYS.hasSession, '1');
  } catch {
    // Private browsing throws on access. The session still works for this tab.
  }
}

export function clearSessionHint(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.hasSession);
  } catch {
    // Nothing to do: the hint is an optimisation, not state we depend on.
  }
}

export function isSessionLikely(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEYS.hasSession) === '1';
  } catch {
    /*
     * Storage unavailable. Assume a session might exist and let the server say
     * otherwise - guessing "no" here would make the portal unusable in a private
     * window, which is a real thing people do.
     */
    return true;
  }
}

/**
 * Forgets everything this tab knows.
 *
 * It cannot delete the refresh cookie - only the server can, and /auth/logout is
 * what asks it to. So a caller that clears tokens without calling logout leaves
 * a live session on the server; that is correct for a 401, where the session is
 * already gone, and wrong as a way to sign somebody out.
 */
export function clearTokens(): void {
  setAccessToken(null);
  clearSessionHint();
}
